import React, { useEffect, useRef } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

/** components */
import CustomTooltip from './CustomTooltip';
import FileSingleItem from './FileSingleItem';
import FileUploader from './FileUploader';

/** helpers */
import { sanitizeHtml } from '../../helpers/format';

export const renderFileStatus = (status) => {
  switch (status) {
    case 'SUBMITTED':
    case 'APPROVED':
      return 'SUBMITTED';
    case 'WAIVED':
      return 'WAIVED';
    default:
      return 'REQUESTED';
  }
};

const FileInput = ({
  field,
  fieldValue,
  fieldError,
  handleSetData,
  handleSetDataError,
  handleSaveFileProperty,
  initializeSectionError,
}) => {
  const NewUploadDisabled =
    (!field.subSection &&
      fieldValue &&
      !!fieldValue[0]?.id &&
      fieldValue[0]?.status != 'DECLINED') ||
    (field.subSection && fieldValue && fieldValue.length == field.limits.maximum);

  const updatedFieldValue = useRef();

  useEffect(() => {
    updatedFieldValue.current = fieldValue;
  }, [fieldValue]);

  const updateFileValue = (activeItemIdx, fileItemContent) => {
    if (field.subSection) {
      // supports multi upload using category
      handleSetData(
        field,
        updatedFieldValue.current.map((fileItem, index) => {
          return index == activeItemIdx ? { ...fileItem, ...fileItemContent } : fileItem;
        })
      );
    } else {
      // supports single upload ( replaces existing file )
      const fileItem = updatedFieldValue.current ? updatedFieldValue.current[0] : {};
      handleSetData(field, [{ ...fileItem, ...fileItemContent }]);
    }
  };

  const handleRemoveItem = (delItemIdx) => {
    handleSetData(
      field,
      updatedFieldValue.current.filter((item, index) => index != delItemIdx)
    );
  };

  const renderFileUploadStatus = () => {
    return (
      <div className="status">
        {fieldValue && fieldValue[0]?.id ? (
          <FontAwesomeIcon fontSize={20} icon={faCircleCheck} />
        ) : (
          <FontAwesomeIcon fontSize={20} icon={faExclamationCircle} color="#ef9851" />
        )}
        <p>
          {fieldValue && (fieldValue[0]?.id || fieldValue[0]?.status == 'WAIVED')
            ? renderFileStatus(fieldValue[0]?.status)
            : 'REQUESTED'}
        </p>
      </div>
    );
  };

  return (
    <React.Fragment key={`file${field.name}`}>
      <div className={`d-flex file-field-box ${fieldError ? 'error' : ''}`}>
        <div className="file-info-section w-100">
          <div className="main-text">
            <div className="file-label">
              <h3>
                <span>{field.label}</span>
                <CustomTooltip field={field} />
                <span className="error-text">{field.required ? ' * ' : ''}</span>
              </h3>
              <span className="file-max-count">
                {field.subSection &&
                field.limits &&
                field.limits.maximum &&
                field.limits.maximum > 1
                  ? `Max ${field.limits.maximum} files`
                  : 'Max 1 file'}
              </span>
            </div>
            {renderFileUploadStatus()}
          </div>
          <div>
            <div className="file-description">
              {field.shortDesc && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(field.shortDesc),
                  }}
                ></span>
              )}
            </div>
            <div className="file-items">
              {fieldValue &&
                Array.isArray(fieldValue) &&
                fieldValue.map((fileItem, idx) => {
                  return (
                    <FileSingleItem
                      key={fileItem.id ? fileItem.id : 'uploading' + fileItem.fileName + idx}
                      field={field}
                      fieldValue={fieldValue}
                      handleSetData={handleSetData}
                      handleSetDataError={handleSetDataError}
                      handleSaveFileProperty={handleSaveFileProperty}
                      initializeSectionError={initializeSectionError}
                      currentFileItem={fileItem}
                      currentItemIndex={idx}
                      updateFileValue={updateFileValue}
                      isNew={fileItem.isNew}
                      isUploadingProps={fileItem.isUploading}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        <FileUploader
          field={field}
          fieldValue={fieldValue}
          handleSetData={handleSetData}
          handleSetDataError={handleSetDataError}
          handleSaveFileProperty={handleSaveFileProperty}
          initializeSectionError={initializeSectionError}
          fileInputName={field.name}
          disabled={NewUploadDisabled}
          updateFileValue={updateFileValue}
          handleRemoveItem={handleRemoveItem}
        />
      </div>
      {fieldError && <p className="error-text">{fieldError}</p>}
    </React.Fragment>
  );
};

export default FileInput;
